import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import Button from "../Button";
import {
    AddRequiredEvidenceTypeFormValidationSchema,
    AddRequiredEvidenceTypeFormValues,
} from "./AddRequiredEvidenceTypeFormValues";
import { useEvidenceTypes } from "../../api/evidence";
import FormDropdown, {
    FormDropdownOption,
    buildOptionsWithSections,
} from "../FormDropdown";
import { Alert } from "@mui/material";
import ContentContainer from "../ContentContainer";

type AddRequiredEvidenceTypeFormProps = {
    onSubmit: (evidenceTypeId: number) => void;
    onCancel: () => void;
    isLoading: boolean;
    requiredEvidenceTypesIds: number[];
};

const AddRequiredEvidenceTypeForm = ({
    onSubmit,
    onCancel,
    isLoading,
    requiredEvidenceTypesIds,
}: AddRequiredEvidenceTypeFormProps) => {
    const methods = useForm<AddRequiredEvidenceTypeFormValues>({
        mode: "onSubmit",
        resolver: yupResolver(AddRequiredEvidenceTypeFormValidationSchema),
    });

    const { evidenceTypes, isLoading: isLoadingEvidenceTypes } =
        useEvidenceTypes();

    const evidenceTypeOptions: FormDropdownOption<number>[] = evidenceTypes
        ? evidenceTypes.map((evidenceType) => ({
              value: evidenceType.id,
              label: evidenceType.name,
              disabled: requiredEvidenceTypesIds.includes(evidenceType.id),
          }))
        : [];

    const evidenceTypeOptionsWithSections = buildOptionsWithSections(
        evidenceTypeOptions,
        (option) => {
            if (requiredEvidenceTypesIds.includes(option.value)) {
                return {
                    sectionTitle: "Already required",
                    sectionOrder: 2,
                };
            }
            return {
                sectionTitle: "Available",
                sectionOrder: 1,
            };
        }
    );

    return (
        <>
            {Object.keys(evidenceTypeOptionsWithSections).length === 0 ? (
                <ContentContainer className="py-10 text-center">
                    Loading...
                </ContentContainer>
            ) : (
                <ContentContainer>
                    {!evidenceTypeOptionsWithSections["Available"] ? (
                        <>
                            <Alert severity="info">
                                All evidence types are currently required.
                            </Alert>
                            <div className="flex justify-end gap-4 pt-7">
                                <Button onClick={onCancel}>Close</Button>
                            </div>
                        </>
                    ) : (
                        <form
                            onSubmit={methods.handleSubmit((values) =>
                                onSubmit(values.evidenceTypeId)
                            )}
                            className="space-y-4"
                        >
                            <div>
                                <Controller
                                    name="evidenceTypeId"
                                    control={methods.control}
                                    render={({
                                        field: { onChange, value },
                                    }) => (
                                        <FormDropdown
                                            required
                                            isLoadingOptions={
                                                isLoadingEvidenceTypes
                                            }
                                            label="Evidence Type"
                                            pleaseSelectText="Select an evidence type"
                                            options={
                                                evidenceTypeOptionsWithSections
                                            }
                                            onChange={onChange}
                                            value={value}
                                            error={
                                                methods.formState.errors
                                                    .evidenceTypeId
                                            }
                                        />
                                    )}
                                />
                            </div>
                            <div className="flex justify-end gap-4 pt-7">
                                <Button
                                    variant="transparent"
                                    type="button"
                                    disabled={isLoading}
                                    onClick={onCancel}
                                >
                                    Cancel
                                </Button>
                                <Button loading={isLoading}>Add</Button>
                            </div>
                        </form>
                    )}
                </ContentContainer>
            )}
        </>
    );
};

export default AddRequiredEvidenceTypeForm;
